import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { distributorInitiativeService } from '../../services/distributor-initiative.service';
import { distributorService } from '../../services/distributor.service';
import type {
  DistributorInitiative,
  DistributorInitiativeInput,
  InitiativePriority,
  InitiativeStatus,
} from '../../types/crm';
import type { Distributor } from '../../types';
import Loading from '../../components/Loading';

const PRIORITIES: InitiativePriority[] = ['low', 'medium', 'high'];
const STATUSES: InitiativeStatus[] = ['planned', 'in_progress', 'completed', 'cancelled'];

// Map enum strings <-> integer values used by Rails enum + ransack `_eq`.
const STATUS_INT: Record<InitiativeStatus, number> = {
  planned: 0,
  in_progress: 1,
  completed: 2,
  cancelled: 3,
};

const PRIORITY_INT: Record<InitiativePriority, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

// react-hook-form returns strings from <select>; coerce in the submit handler.
const schema = z.object({
  distributor_id: z.string().min(1, 'Distributor is required'),
  target_month: z.string().min(1, 'Target month is required'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high']),
  target_revenue: z.string().optional(),
  target_cases: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const titleize = (v: string) => v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const monthToFirst = (value: string): string => (value.length === 7 ? `${value}-01` : value);

export const DistributorInitiatives = () => {
  const [initiatives, setInitiatives] = useState<DistributorInitiative[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filters
  const [distributorFilter, setDistributorFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<InitiativeStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<InitiativePriority | ''>('');
  const [monthFilter, setMonthFilter] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { priority: 'medium' },
  });

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    void loadInitiatives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorFilter, statusFilter, priorityFilter, monthFilter]);

  const loadLookups = async () => {
    try {
      const result = await distributorService.list({ per_page: 200 });
      setDistributors(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const loadInitiatives = async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (distributorFilter) params['q[distributor_id_eq]'] = distributorFilter;
      if (statusFilter) params['q[status_eq]'] = STATUS_INT[statusFilter];
      if (priorityFilter) params['q[priority_eq]'] = PRIORITY_INT[priorityFilter];
      if (monthFilter) {
        const first = monthToFirst(monthFilter);
        params['q[target_month_gteq]'] = first;
        params['q[target_month_lteq]'] = first;
      }
      const result = await distributorInitiativeService.getAll(params);
      setInitiatives(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onCreate = async (data: FormData) => {
    setIsCreating(true);
    try {
      const distributorId = Number(data.distributor_id);
      if (!Number.isFinite(distributorId)) {
        toast.error('Please choose a distributor.');
        return;
      }

      const input: DistributorInitiativeInput = {
        distributor_id: distributorId,
        target_month: monthToFirst(data.target_month),
        title: data.title,
        description: data.description || null,
        priority: data.priority,
        target_revenue: data.target_revenue || null,
        target_cases: data.target_cases || null,
      };
      await distributorInitiativeService.create(input);
      toast.success('Initiative created');
      setShowCreateModal(false);
      reset({ priority: 'medium' });
      void loadInitiatives();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Distributor initiatives</h1>
          <p className="text-sm text-gray-600 mt-1">Monthly initiatives at the distributor level.</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add initiative
        </button>
      </div>

      <div className="card p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="label">Distributor</label>
          <select
            value={distributorFilter}
            onChange={(e) => setDistributorFilter(e.target.value)}
            className="input"
          >
            <option value="">All distributors</option>
            {distributors.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as InitiativeStatus | '')}
            className="input"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{titleize(s)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Priority</label>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as InitiativePriority | '')}
            className="input"
          >
            <option value="">All priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{titleize(p)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Month</label>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="input"
          />
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8"><Loading /></div>
        ) : initiatives.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No initiatives found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distributor</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue / cases</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {initiatives.map((i) => (
                  <tr key={i.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{i.target_month}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{i.distributor_name || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{i.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        i.priority === 'high' ? 'bg-red-100 text-red-800' :
                        i.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {titleize(i.priority)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        i.status === 'completed' ? 'bg-green-100 text-green-800' :
                        i.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        i.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {titleize(i.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {[i.target_revenue, i.target_cases].filter(Boolean).join(' / ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-(--color-bg-scrim)" onClick={() => setShowCreateModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onCreate)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add initiative</h3>
                    <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="distributor_id" className="label">Distributor</label>
                      <select {...register('distributor_id')} id="distributor_id" className="input">
                        <option value="">Choose a distributor…</option>
                        {distributors.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                      {errors.distributor_id && <p className="mt-1 text-sm text-red-600">{errors.distributor_id.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="title" className="label">Title</label>
                      <input {...register('title')} id="title" type="text" className="input" />
                      {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="target_month" className="label">Target month</label>
                        <input {...register('target_month')} id="target_month" type="month" className="input" />
                        {errors.target_month && <p className="mt-1 text-sm text-red-600">{errors.target_month.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="priority" className="label">Priority</label>
                        <select {...register('priority')} id="priority" className="input">
                          {PRIORITIES.map((p) => (
                            <option key={p} value={p}>{titleize(p)}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="target_revenue" className="label">Revenue goal</label>
                        <input {...register('target_revenue')} id="target_revenue" type="text" placeholder="0.00" className="input" />
                      </div>
                      <div>
                        <label htmlFor="target_cases" className="label">Case goal</label>
                        <input {...register('target_cases')} id="target_cases" type="text" placeholder="0.00" className="input" />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="description" className="label">Description</label>
                      <textarea {...register('description')} id="description" rows={3} className="input" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isCreating} className="btn btn-primary">
                    {isCreating ? 'Creating…' : 'Create initiative'}
                  </button>
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistributorInitiatives;
